import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BillingOrder } from '../models/billingorder.model';
import { Lease, CodeAndHash } from '../models/lease.model';
import { AchPaymentMethod, Card } from '../models/payment.model';
import { IScrBaseResponse, ScrBaseService } from './base.service';
import { ScrServiceManager } from './servicemanager.service';
import { HttpClient } from '@angular/common/http';
import { WITHCRED } from './authentication.service';


import { defaults } from 'lodash-es';
import { SelectType, OrderTransactionType } from '../models/constants';
import { MonthlyInvoice } from '../models/monthlyinvoice.model';
import { NGXLogger } from 'ngx-logger';
import { ScrEnvironment } from 'src/environments/environment';


export class PendingCartOrder {
  constructor(public pendOrder: BillingOrder) { }
}

export class PaymentPageState {

  public payNowType = true;
  public selCardId = 0;
  public applyCredit = false;
  public lastPullCreditTime = 0;
  public creditsHaveBeenPulled = false;
}

export class OrderTransaction {
  transactionType: OrderTransactionType;
  transactionOrder: BillingOrder;
  constructor(tOrder: BillingOrder, tType: OrderTransactionType) { // whenever it is created - should be current timestamp, then update it as we update the structure
    this.transactionType = tType;
    this.transactionOrder = tOrder;
    this.transactionOrder.leases = tOrder.leases || new Array<Lease>();
    this.transactionOrder.numberOfCodes = this.transactionOrder.leases.length;
  }
}

export class MarketingBrochureStorage {
  selectcodes!: string[];
  randomcodes: number | CodeAndHash[];
  timestamp: number;
  version: number;

  constructor() {
    this.timestamp = new Date().getTime();
    this.version = 2;
    this.randomcodes = [];
  }
}


export interface HashCode {
  code: string;
  hash: string;
}

export interface IOrderResponse extends IScrBaseResponse {
  /**
   * Upon success, the updated order.
   */
  order: BillingOrder;

  /**
   * metadata
   * - for now count of number of orders for this account to be used in analytics feed.
   */
  meta: {
    orderCount: number;
  }
}


export interface IMonthlyInvoiceResponse extends IScrBaseResponse {
  monthlyInvoice: MonthlyInvoice;
}
@Injectable({
  providedIn: 'root'
})
export class PendingOrderService extends ScrBaseService {

  public pricingHash = '';
  public keepTheCode = true;

  orderTransaction$: BehaviorSubject<OrderTransaction>;
  constructor(
    private http: HttpClient,
    private logger: NGXLogger,
    private environment: ScrEnvironment,
    serviceManager: ScrServiceManager) {
    super(serviceManager);

    this.pricingHash = window.localStorage.pricingHash;
    this.orderTransaction$ = new BehaviorSubject(new OrderTransaction(new BillingOrder(), OrderTransactionType.New));
  }

  emitChangePendOrder(ot: OrderTransaction): void {
    if (ot.transactionType === OrderTransactionType.New) {
      this.saveMarketingBrochureStorage(ot.transactionOrder);
      const epochTime = this.getMarketingBrochureStorage()?.timestamp || (new Date()).getTime();
      ot.transactionOrder.updateTime = new Date(new Date(epochTime).getTime());
      window.localStorage.pendingOrderStorage = JSON.stringify(ot.transactionOrder);
    }
  }

  /**
   * Utility to save MB.
   */
  storeMarketingBrochureContent(mbObj?: MarketingBrochureStorage): void {
    if (!mbObj) {
      window.localStorage.removeItem('ngStorage-scr_codes');
    }
    else {
      window.localStorage['ngStorage-scr_codes'] = JSON.stringify(mbObj);
    }
  }

  /**
   * Save the contents of the pending order into the
   * marketing brochure compatible local storage.
   * @param po - pending order
   */
  saveMarketingBrochureStorage(po: BillingOrder): void {

    const mbObj = new MarketingBrochureStorage();

    mbObj.randomcodes = po.leases.filter(l => l.selectType === SelectType.Random).map(l => {
      return {
        code: l.scId,
        hash: l.shortCodeHash ?? ''
      };
    });
    mbObj.selectcodes = po.leases.filter(l => l.selectType === SelectType.Selected).map(lease => `${lease.scId}`);

    //mb comes with timestamp from constructor
    this.logger.trace("saved time in mb storage = " + mbObj.timestamp);

    if (po.leases.length > 0) {
      window.localStorage['ngStorage-scr_codes'] = JSON.stringify(mbObj);
    }
    else {
      // there are no codes in the order so clear it.
      window.localStorage.removeItem('ngStorage-scr_codes');
    }
  }


  emitChangeLeaseApplication(la: Lease): void {
    la = this.deleteEmpties(la);
    window.localStorage.leaseApplicationStorage = JSON.stringify(la);
  }

  removeLeaseApplication(): void {
    window.localStorage.leaseApplicationStorage = JSON.stringify(new Lease());
  }

  removePendingOrder(): void {
    window.localStorage.pendingOrderStorage = JSON.stringify(new BillingOrder());
    this.removeMarketBrochureStorage();
    this.setOrderTransactionDetails(OrderTransactionType.New, new BillingOrder()); //Should never be empty
  }

  removeMarketBrochureStorage(): void {
    window.localStorage.removeItem('ngStorage-scr_codes');
    this.logger.debug('MB storage is emptied out now = ' + window.localStorage['ngStorage-scr_codes']);
  }

  getCartCount(): number {
    const mbObj = this.getMarketingBrochureStorage();
    let cartCount = 0;
    if (Array.isArray(mbObj?.randomcodes)) {
      cartCount = (mbObj?.randomcodes?.length || 0) + (mbObj?.selectcodes?.length || 0);
    }
    else {
      cartCount = (mbObj?.randomcodes || 0) + (mbObj?.selectcodes?.length || 0);
    }
    return cartCount;
  }

  getMarketingBrochureStorage(): MarketingBrochureStorage | undefined {
    if (window.localStorage['ngStorage-scr_codes']) {
      return JSON.parse(window.localStorage['ngStorage-scr_codes']);
    }
    return undefined;
  }

  removeAllOrder(oType: OrderTransactionType): void {
    if (oType === OrderTransactionType.New) {
      this.removePendingOrder();
      this.removeLeaseApplication();
      this.removePaymentPageState();
    }
  }

  /**
   * Clear saved pending order data.
   */
  clear(): void {
    window.localStorage.removeItem('paymentStorage');
    window.localStorage.removeItem('pendingOrderStorage');
    window.localStorage.removeItem('leaseApplicationStorage');
  }

  createBillingOrderToComplete(
    order: BillingOrder, /*, application: Lease*/
    transType: OrderTransactionType
  ): BillingOrder {

    order.leases = order.leases.filter(l => l.selected).map(lease => {
      lease.addToMb = order.enrollInMonthlyBilling;
      lease.autorenew = order.enrollInAutoRenew;
      if (transType === OrderTransactionType.New) {
        const application = window.localStorage.leaseApplicationStorage ? JSON.parse(window.localStorage.leaseApplicationStorage) : {};
        defaults(lease, application);
      }

      lease = this.deleteEmpties(lease);
      return lease;
    });

    order = this.deleteEmpties(order);
    return order;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deleteEmpties(o: any): any {
    Object.keys(o).forEach((key) => {
      if ((o[key] && typeof o[key] === 'object')) {
        this.deleteEmpties(o[key]);
      }
      else {
        if ((o[key] === '' || o[key] === null)) {
          delete o[key];
        }
      }
    });
    return o;
  }

  getPendingOrder(): BillingOrder {
    let o = new BillingOrder();
    if (window.localStorage.pendingOrderStorage) {
      o = JSON.parse(window.localStorage.pendingOrderStorage);
    }
    return o;
  }

  getLeaseApplication(): Lease {
    let l = new Lease();
    if (window.localStorage.leaseApplicationStorage) {
      l = JSON.parse(window.localStorage.leaseApplicationStorage);
    }
    return l;
  }


  removePaymentPageState(): void {
    this.logger.trace('removing payment page state');
    window.localStorage.removeItem('paymentStorage');
  }

  savePaymentPageState(state: PaymentPageState): void {
    this.logger.trace('saving payment page state ' + JSON.stringify(state));
    window.localStorage.setItem('paymentStorage', JSON.stringify(state));
  }

  getPaymentPageState(): PaymentPageState {
    let p = new PaymentPageState(); // default settings
    const s = window.localStorage.getItem('paymentStorage');
    if (s) {
      p = JSON.parse(s);
    }
    return p;
  }

  saveOrder(pkc: boolean): void {
    this.keepTheCode = pkc;
  }

  /**
   * This is called when we are leaving the checkout pages.
   * We can use this time to clean up any data we don't need
   * to keep around.
   */
  public leavingCheckoutPages(): void {
    this.logger.trace('user is leaving checkout pages');
    this.removePaymentPageState();
  }

  setOrderTransactionDetails(tranType: OrderTransactionType, orderInTransaction: BillingOrder) {
    const ot = new OrderTransaction(orderInTransaction, tranType,);
    this.orderTransaction$.next(ot);
  }
  /**
   * Submit an order.
   * @param paymentMode - either 'paynow' or 'paylater'
   * @param ach - if paying with an ACH.
   * @param creditToBeApplied - optional amount of account create to use towards payment.
   * @returns observable to monitor for response.
   */
  submitOrder(paymentMode: string, order: BillingOrder, cc?: Card, ach?: AchPaymentMethod, creditToBeApplied?: number): Observable<IOrderResponse> {
    const url = `${this.environment.base}/fct/billingorder/pay`;
    const body = this.functionUrlBody(paymentMode, { cc, ach, order, creditToBeApplied });
    const strBody = JSON.stringify(body, ScrBaseService.phoneReplacer);
    return this.http.post<IOrderResponse>(url, strBody, WITHCRED);
  }

  /**
   * Pay an order that is due to be paid.
   * 
   * @param order - payment due order
   * @param cc - card to pay with
   * @param ach - if paying with an ACH.
   * @param creditToBeApplied - optional amount of account create to use towards payment.
   * @returns observable to monitor for response.
   */
  payForOrder(order: BillingOrder, cc?: Card, ach?: AchPaymentMethod, creditToBeApplied?: number): Observable<IOrderResponse> {
    const url = `${this.environment.base}/fct/billingorder/pay`;
    const body = this.functionUrlBody('pay', { cc, ach, order, creditToBeApplied });
    const strBody = JSON.stringify(body, ScrBaseService.phoneReplacer);
    return this.http.post<IOrderResponse>(url, strBody, WITHCRED);
  }



  /**
   * Manual pay monthly invoice.
   * 
   * @param monthlyInvoice - invoice to be paid.
   * @param cc - card to pay with
   * @param ach - if paying with an ACH.
   * @param creditToBeApplied - optional amount of account create to use towards payment.
   * @returns observable to monitor for response.
   */
  payForMonthlyInvoice(monthlyInvoice: MonthlyInvoice, cc?: Card, ach?: AchPaymentMethod, creditToBeApplied?: number): Observable<IMonthlyInvoiceResponse> {
    const url = `${this.environment.base}/fct/monthlyinvoice/pay`;
    const body = this.functionUrlBody('pay', { cc, ach, monthlyInvoice, creditToBeApplied });
    const strBody = JSON.stringify(body, ScrBaseService.phoneReplacer);
    return this.http.post<IMonthlyInvoiceResponse>(url, strBody, WITHCRED);
  }

}
